"use client";

import { ArrowDown } from "lucide-react";

import type { BuilderCvCheck } from "@/types/builder-cv-check";
import { hasSubstance } from "@/lib/cv-builder/text-substance";
import { cn } from "@/lib/utils";

interface PolishPreviewCardProps {
  sample: BuilderCvCheck["sample_rewrite"];
  /** Copy above the pairs. Varies by surface (inline pitch vs dialog). */
  heading?: string;
  className?: string;
}

interface PreviewPairProps {
  label: string;
  before: string;
  after: string;
}

/**
 * One before/after pair. The "before" is the user's own words, so it is shown
 * plainly rather than mocked; the "after" carries the emphasis because it is
 * the thing being sold.
 */
function PreviewPair({ label, before, after }: PreviewPairProps) {
  return (
    <div className="grid gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>

      <div className="rounded-lg border border-border bg-background p-3">
        <p className="text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground">
          CV kamu sekarang
        </p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{before}</p>
      </div>

      <div className="flex justify-center" aria-hidden="true">
        <ArrowDown className="h-3.5 w-3.5 text-primary" />
      </div>

      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
        <p className="text-[0.65rem] font-medium uppercase tracking-wide text-primary">
          Setelah AI Polish
        </p>
        <p className="mt-1 text-xs font-medium leading-5 text-foreground">
          {after}
        </p>
      </div>
    </div>
  );
}

/**
 * Free preview of the paid AI Polish, built from the sample the readiness check
 * already returns. Shown before any payment so the buyer sees the output
 * instead of only a list of problems.
 */
export function PolishPreviewCard({
  sample,
  heading = "Contoh hasilnya, dari CV kamu sendiri",
  className,
}: PolishPreviewCardProps) {
  // Both sides must be real. With nothing to work from the model writes
  // generic guidance ("Tuliskan ringkasan singkat yang..."), and presenting
  // that as "Setelah AI Polish" sells filler as the product.
  const hasSummary =
    hasSubstance(sample.summary_before) && hasSubstance(sample.summary_after);
  const hasBullet =
    hasSubstance(sample.bullet_before) && hasSubstance(sample.bullet_after);

  if (!hasSummary && !hasBullet) {
    return null;
  }

  return (
    <div className={cn("rounded-2xl border border-border bg-card p-4", className)}>
      <p className="text-sm font-semibold text-foreground">{heading}</p>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">
        Ini contoh gratis. AI Polish mengerjakan seluruh ringkasan dan semua
        bullet pengalaman dengan cara yang sama.
      </p>

      <div className="mt-4 grid gap-5">
        {hasSummary ? (
          <PreviewPair
            label="Ringkasan"
            before={sample.summary_before}
            after={sample.summary_after}
          />
        ) : null}
        {hasBullet ? (
          <PreviewPair
            label="Bullet pengalaman"
            before={sample.bullet_before}
            after={sample.bullet_after}
          />
        ) : null}
      </div>
    </div>
  );
}
